import TiktokPixel from "tiktok-pixel";

import type { User } from "../types/api";

declare global {
  interface Window {
    ttq?: { identify?: (data: Record<string, unknown>) => void };
  }
}

const PIXEL_ID = process.env.REACT_APP_TIKTOK_PROJECT_TOKEN;

/** Match Mixpanel: production builds only. */
const isTiktokRuntimeEnabled =
  process.env.NODE_ENV === "production" && Boolean(PIXEL_ID);

let pixelScriptStarted = false;

function ttqIdentify(data: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const ttq = window.ttq;
  if (ttq && typeof ttq.identify === "function") {
    ttq.identify(data);
  }
}

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem("user_data");
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

/**
 * Initialize TikTok Pixel once the user is known (advanced matching), and refresh
 * identity on later calls (e.g. account switch) without relying on a second `init`
 * load (the `tiktok-pixel` helper only injects the script the first time).
 */
export function initTiktokPixel(user: User | null): void {
  if (!isTiktokRuntimeEnabled || !user || !PIXEL_ID) {
    return;
  }

  const advancedMatching = {
    email: user.email,
    external_id: String(user.id),
  };
  const options = { debug: Boolean(process.env.REACT_APP_TIKTOK_DEBUG) };

  if (!pixelScriptStarted) {
    pixelScriptStarted = true;
    void TiktokPixel.init(PIXEL_ID, advancedMatching, options);
    return;
  }

  ttqIdentify(advancedMatching);
}

/**
 * Forward analytics-style event names to TikTok (same pattern as `trackPixelEvent`
 * in other apps). Requires a prior {@link initTiktokPixel} while logged in.
 */
export function trackTiktokPixelEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (!isTiktokRuntimeEnabled || !pixelScriptStarted) {
    return;
  }

  const user = readStoredUser();
  const user_email = user?.email ?? "";

  TiktokPixel.track(
    eventName as never,
    {
      ...(properties ?? {}),
      ...(user_email ? { user_email } : {}),
    } as never,
  );
}

/**
 * TikTok standard purchase / payment completion (maps to the common `PURCHASE` ad pixel use case).
 * Uses {@link CompletePayment} so value and currency are attributed correctly.
 */
export function trackTiktokCompletePayment(payload: {
  value: number;
  currency: "USD";
  content_id?: string;
}): void {
  if (!isTiktokRuntimeEnabled || !pixelScriptStarted) {
    return;
  }

  const user = readStoredUser();
  const user_email = user?.email ?? "";

  TiktokPixel.track("CompletePayment", {
    value: payload.value,
    currency: payload.currency,
    ...(payload.content_id ? { content_id: payload.content_id } : {}),
    ...(user_email ? { user_email } : {}),
  } as never);
}

/** Clear advanced matching on logout; pixel script stays loaded for the SPA session. */
export function resetTiktokPixel(): void {
  if (!isTiktokRuntimeEnabled) {
    return;
  }
  ttqIdentify({});
}
